/**
 * CiSelector — universal CI type/id selector for polymorphic relationships.
 *
 * Usage:
 *   <CiSelector
 *     ciType="rack_element"          // current value (optional)
 *     ciId={42}                      // current value (optional)
 *     companyId={1}
 *     fixedType="rack_element"       // optional: hide type dropdown, only search
 *     typeOptions={[{value,label}]}  // optional: limita los tipos ofrecidos
 *     onSelect={({ ci_type, ci_id, name, description }) => ...}
 *     onClear={() => ...}
 *   />
 *
 * `typeOptions` sustituye la lista que se carga del backend: sirve cuando sólo
 * valen unos tipos concretos (p. ej. una licencia sólo se puede asignar a un
 * servidor, una VM, un servicio o una instancia de BD). Si sólo hay uno, el
 * desplegable de tipo se oculta (como `fixedType`).
 *
 * Renders two fields:
 *   1. A dropdown to choose the CI type (server, VM, printer, WiFi, etc.)
 *   2. A search/autocomplete input to find a specific CI of that type
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

const CI_ICONS = {
  rack_element:      '🖥️',
  virt_vm:           '💻',
  printer:           '🖨️',
  wifi_controller:   '📡',
  wifi_access_point: '📶',
  firewall:          '🛡️',
  router:            '🌐',
  ups:               '🔋',
  workstation:       '🖥️',
  device:            '📟',
  storage_disk:      '💾',
  isp_circuit:       '🔌',
  service:           '⚙️',
  ipam_ip:           '📟',
  database_instance: '🗄️',
};

// Ordered list of most common CI types for the dropdown
const COMMON_CI_TYPES = [
  'rack_element', 'virt_vm', 'printer', 'wifi_controller',
  'firewall', 'router', 'ups', 'workstation', 'ipam_ip',
];

export default function CiSelector({ ciType, ciId, companyId, onSelect, onClear, fixedType, typeOptions }) {
  // Con una sola opción permitida no tiene sentido el desplegable de tipo.
  const opcionesOfrecidas = typeOptions && typeOptions.length ? typeOptions : null;
  const tipoFijo = fixedType || (opcionesOfrecidas && opcionesOfrecidas.length === 1 ? opcionesOfrecidas[0].value : undefined);
  const [selectedType, setSelectedType] = useState(tipoFijo || ciType || '');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Initialize from props
  useEffect(() => {
    if (ciType && ciId) {
      setSelected({ ci_type: ciType, ci_id: ciId, name: `#${ciId}`, description: null });
      setSelectedType(ciType);
    }
  }, [ciType, ciId]);

  // Load CI types meta for the dropdown
  const [ciTypes, setCiTypes] = useState([]);
  useEffect(() => {
    if (!companyId || opcionesOfrecidas) return;
    api.getServiceAccessPointsMeta(companyId).then(m => {
      if (m.ci_types) setCiTypes(m.ci_types);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const tipos = opcionesOfrecidas || ciTypes || [];

  // Search when search text changes
  const doSearch = useCallback(async (type, q) => {
    if (!type || !q || q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.ciSearch(companyId, { types: type, q, limit: 10 });
      setResults(res.results || []);
      setShowDropdown(true);
    } catch (_) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length >= 2 && selectedType) {
      debounceRef.current = setTimeout(() => doSearch(selectedType, val), 300);
    } else {
      setResults([]);
      setShowDropdown(false);
    }
  };

  const handleSelect = (ci) => {
    setSelected(ci);
    setSearch(ci.name);
    setShowDropdown(false);
    onSelect(ci);
  };

  const handleClear = () => {
    setSelected(null);
    setSearch('');
    setResults([]);
    setShowDropdown(false);
    onClear();
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    if (!selected || selected.ci_type !== type) {
      setSelected(null);
      setSearch('');
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div className="row g-1">
        {/* CI Type dropdown (hidden when fixedType is set) */}
        {tipoFijo ? (
          <div className="col-md-4">
            <div style={{
              padding: '2px 8px', fontSize: '0.82rem', height: '100%',
              display: 'flex', alignItems: 'center', gap: 4,
              color: 'var(--text-secondary)',
            }}>
              <span>{CI_ICONS[tipoFijo] || '📦'}</span>
              <span>{(tipos || []).find(t => t.value === tipoFijo)?.label || tipoFijo}</span>
            </div>
          </div>
        ) : (
          <div className="col-md-4">
            <select
              className="form-select form-select-sm"
              value={selectedType}
              onChange={e => handleTypeChange(e.target.value)}
            >
              <option value="">— tipo de CI —</option>
              {opcionesOfrecidas
                ? opcionesOfrecidas.map(t => (
                  <option key={t.value} value={t.value}>
                    {CI_ICONS[t.value] || '📦'} {t.label}
                  </option>
                ))
                : (
                  <>
                    {(tipos || []).filter(t => COMMON_CI_TYPES.includes(t.value)).map(t => (
                      <option key={t.value} value={t.value}>
                        {CI_ICONS[t.value] || '📦'} {t.label}
                      </option>
                    ))}
                    <option disabled>───</option>
                    {(tipos || []).filter(t => !COMMON_CI_TYPES.includes(t.value)).map(t => (
                      <option key={t.value} value={t.value}>
                        {CI_ICONS[t.value] || '📦'} {t.label}
                      </option>
                    ))}
                  </>
                )}
            </select>
          </div>
        )}

        {/* Search / Selected display */}
        <div className="col-md-6" style={{ position: 'relative' }}>
          {selected && search === selected.name ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '2px 8px', background: 'var(--green-50)', border: '1px solid var(--green-600)',
              borderRadius: 4, fontSize: '0.82rem', height: '100%',
            }}>
              <span>{CI_ICONS[selected.ci_type] || '📦'}</span>
              <span style={{ fontWeight: 600 }}>{selected.name}</span>
              {selected.description && (
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.72rem', marginLeft: 4 }}>
                  · {selected.description}
                </span>
              )}
              <button
                type="button"
                onClick={handleClear}
                style={{
                  marginLeft: 'auto', border: 'none', background: 'none',
                  cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.85rem',
                  padding: '0 4px',
                }}
                title="Quitar selección"
              >
                ✕
              </button>
            </div>
          ) : (
            <input
              ref={searchRef}
              className="form-control form-control-sm"
              value={search}
              onChange={handleSearchChange}
              placeholder={selectedType ? `Buscar ${selectedType.replace('_', ' ')}…` : 'Selecciona un tipo de CI primero'}
              disabled={!selectedType}
              onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            />
          )}

          {/* Search results dropdown */}
          {showDropdown && results.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
                background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxHeight: 200, overflowY: 'auto',
              marginTop: 2,
            }}>
              {results.map((ci, idx) => (
                <div
                  key={`${ci.ci_type}-${ci.ci_id}-${idx}`}
                  onMouseDown={() => handleSelect(ci)}
                  style={{
                    padding: '8px 12px', cursor: 'pointer', display: 'flex',
                    alignItems: 'center', gap: 8, fontSize: '0.85rem',
                    borderBottom: '1px solid var(--border)',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'var(--bg-toolbar)'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span>{CI_ICONS[ci.ci_type] || '📦'}</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>{ci.name}</div>
                    {ci.description && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{ci.description}</div>
                    )}
                  </div>
                    <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                    {ci.ci_type_label}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Loading indicator */}
          {loading && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
              background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6,
              padding: '8px 12px', fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2,
            }}>
              Buscando…
            </div>
          )}

          {/* No results */}
          {showDropdown && search.length >= 2 && results.length === 0 && !loading && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
              background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6,
              padding: '8px 12px', fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2,
            }}>
              Sin resultados para "{search}"
            </div>
          )}
        </div>

        {/* CI ID display */}
        <div className="col-md-2" style={{ display: 'flex', alignItems: 'center' }}>
          {selected && (
            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              ID: {selected.ci_id}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
